"use client"
import type React from "react"
import { useEffect, useState } from "react"
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Fab,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Badge,
} from "@mui/material"
import {
  Add as AddIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material"
import { ServiceRequestModal } from "./ServiceRequestModal"
// import type { ServiceRequest } from "../../types/services"
// import { createServices, getServices } from "../../api/services/services"
import { IonContent, IonPage } from "@ionic/react"
import { createServices, getServices } from "../../../api/services/services"
import { ServiceRequest } from "./types/services"
import { formatCurrencyN } from "../../../common/utils_t"

interface APIServiceRequest {
  id: number
  user_id: string
  service_type: string
  service_type_id: "recruitment" | "hr-outsourcing"
  selected_services: string[]
  selected_items: { [key: string]: string[] }
  status: "draft" | "pending" | "assigned" | "in-progress" | "completed" | "rejected"
  created_at: string
  project_id?: string | null
}

interface APIProject {
  project_id: string | null
  project_name: string
  tags: string
  budget_cost: string
  status: "PENDING" | "IN_PROGRESS" | "COMPLETED" | "ON_HOLD"
  created_at: string
  business_id?: string | null
  jobRoles?: any[]
}

interface APIResponse {
  status: boolean
  data: {
    intakes: APIServiceRequest[]
    projects: APIProject[]
  }
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  )
}

export const BusinessRequestPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [projects, setProjects] = useState<APIProject[]>([])
  const [intakes, setIntakes] = useState<APIServiceRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tabValue, setTabValue] = useState(0)

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "warning"
      case "assigned":
        return "info"
      case "in-progress":
      case "in_progress":
        return "primary"
      case "completed":
        return "success"
      case "rejected":
        return "error"
      case "draft":
        return "default"
      case "on_hold":
        return "secondary"
      default:
        return "default"
    }
  }

  // Helper function to parse tags from JSON string
  const parseTags = (tagsString: string): string[] => {
    try {
      if (!tagsString) return []
      const cleanedString = tagsString.replace(/^"|"$/g, "").replace(/\\"/g, '"')
      return JSON.parse(cleanedString) || []
    } catch (error) {
      console.error("Error parsing tags:", error)
      return []
    }
  }

  const handleRequestSubmit = async (request: Omit<ServiceRequest, "id" | "createdAt" | "status">) => {
    try {
      setLoading(true)
      const response = await createServices(request)
      console.log("Service request submitted:", response)

      if (response.status) {
        await getRequests()
        setIsModalOpen(false)
      } else {
        throw new Error(response.message || "Failed to submit request")
      }
    } catch (error) {
      console.error("Error submitting request:", error)
      setError("Failed to submit request. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const getRequests = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await getServices()
      console.log("API Response:", response)

      if (response.status === true) {
        setProjects(response.data.projects || [])
        setIntakes(response.data.intakes || [])
      } else {
        throw new Error("Failed to fetch requests")
      }
    } catch (error) {
      console.error("Error fetching requests:", error)
      setError("Failed to load requests. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getRequests()
  }, [])


  console.log({projects})
  if (loading && projects.length === 0 && intakes.length === 0) {
    return (
      <IonPage>
        <IonContent>
          <Container maxWidth="lg" sx={{ py: 6 }}>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
              <Box sx={{ textAlign: "center" }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography>Loading your requests...</Typography>
              </Box>
            </Box>
          </Container>
        </IonContent>
      </IonPage>
    )
  }

  return (
    <IonPage>
      <IonContent>
        <Container maxWidth="lg" sx={{ py: 6 }}>
          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Hero Section */}
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography variant="h2" component="h1" sx={{ fontWeight: 700, mb: 3, color: "#000" }}>
              Transform Your Business
            </Typography>
            <Typography variant="h5" sx={{ mb: 6, color: "#666", maxWidth: "800px", mx: "auto", lineHeight: 1.6 }}>
              Choose from automated recruitment solutions or comprehensive HR outsourcing services tailored to your
              business needs
            </Typography>

            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => setIsModalOpen(true)}
              disabled={loading}
              sx={{
                bgcolor: "#000",
                color: "white",
                px: 4,
                py: 2,
                fontSize: "1.1rem",
                fontWeight: 600,
                "&:hover": { bgcolor: "#333" },
                "&:disabled": { bgcolor: "#ccc" },
                borderRadius: 2,
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Make a Service Request"}
            </Button>
          </Box>

          {/* Service Overview Cards */}
          <Grid container spacing={4} sx={{ mb: 8 }}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: "100%",
                  border: "2px solid #000",
                  borderRadius: 3,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Avatar sx={{ bgcolor: "#000", mr: 3, width: 64, height: 64 }}>
                      <WorkIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: "#000" }}>
                        Recruitment Projects
                      </Typography>
                      <Badge badgeContent={projects.length} color="primary" sx={{ mt: 1 }}>
                        <Chip label="Active Projects" variant="outlined" />
                      </Badge>
                    </Box>
                  </Box>
                  <Typography variant="body1" sx={{ mb: 4, color: "#666", lineHeight: 1.7, fontSize: "1.1rem" }}>
                    Create recruitment projects with AI-powered matching, automated screening, and comprehensive
                    candidate management for efficient hiring.
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                    <Chip
                      label="Project-based"
                      variant="outlined"
                      sx={{ borderColor: "#000", color: "#000", fontWeight: 600 }}
                    />
                    <Chip
                      label="AI Matching"
                      variant="outlined"
                      sx={{ borderColor: "#000", color: "#000", fontWeight: 600 }}
                    />
                    <Chip
                      label="Auto Screening"
                      variant="outlined"
                      sx={{ borderColor: "#000", color: "#000", fontWeight: 600 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: "100%",
                  border: "2px solid #000",
                  borderRadius: 3,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Avatar sx={{ bgcolor: "#000", mr: 3, width: 64, height: 64 }}>
                      <PeopleIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: "#000" }}>
                        HR Service Intakes
                      </Typography>
                      <Badge badgeContent={intakes.length} color="primary" sx={{ mt: 1 }}>
                        <Chip label="Service Requests" variant="outlined" />
                      </Badge>
                    </Box>
                  </Box>
                  <Typography variant="body1" sx={{ mb: 4, color: "#666", lineHeight: 1.7, fontSize: "1.1rem" }}>
                    Comprehensive HR solutions with 11 specialized service categories, fully customizable to your
                    business requirements with expert consultation.
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                    <Chip
                      label="11 Service Categories"
                      variant="outlined"
                      sx={{ borderColor: "#000", color: "#000", fontWeight: 600 }}
                    />
                    <Chip
                      label="Fully Customizable"
                      variant="outlined"
                      sx={{ borderColor: "#000", color: "#000", fontWeight: 600 }}
                    />
                    <Chip
                      label="Expert Consultation"
                      variant="outlined"
                      sx={{ borderColor: "#000", color: "#000", fontWeight: 600 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tabs for Projects and Service Intakes */}
          {(projects.length > 0 || intakes.length > 0) && (
            <Box sx={{ mb: 6 }}>
              <Paper sx={{ border: "2px solid #000", borderRadius: 2 }}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    value={tabValue}
                    onChange={(_, newValue) => setTabValue(newValue)}
                    sx={{
                      bgcolor: "#000",
                      "& .MuiTab-root": {
                        color: "white",
                        fontWeight: 600,
                        "&.Mui-selected": {
                          color: "white",
                        },
                      },
                      "& .MuiTabs-indicator": {
                        backgroundColor: "white",
                      },
                    }}
                  >
                    <Tab
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <BusinessIcon />
                          <span>Recruitment Projects ({projects.length})</span>
                        </Box>
                      }
                    />
                    <Tab
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <AssignmentIcon />
                          <span>HR Service Intakes ({intakes.length})</span>
                        </Box>
                      }
                    />
                  </Tabs>
                </Box>

                {/* Projects Tab */}
                <TabPanel value={tabValue} index={0}>
                  {projects.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}> S/N</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Project Name</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Tags</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Budget</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Created Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {projects.map((project, index) => (
                            <TableRow key={index} sx={{ "&:hover": { bgcolor: "#f8f8f8" } }}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                                  {`#${index + 1}`}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: "#000" }}>
                                  {project.project_name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {parseTags(project.tags)
                                    .slice(0, 3)
                                    .map((tag, tagIndex) => (
                                      <Chip
                                        key={tagIndex}
                                        label={tag}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: "0.7rem", height: "20px" }}
                                      />
                                    ))}
                                  {parseTags(project.tags).length > 3 && (
                                    <Chip
                                      label={`+${parseTags(project.tags).length - 3}`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: "0.7rem", height: "20px" }}
                                    />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {(project.budget_cost)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={project?.status?.replace("_", " ").toUpperCase()}
                                  color={getStatusColor(project?.status) as any}
                                  variant="filled"
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {new Date(project.created_at).toLocaleDateString()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 8 }}>
                      <BusinessIcon sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No recruitment projects yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Create your first recruitment project to get started
                      </Typography>
                    </Box>
                  )}
                </TabPanel>

                {/* Service Intakes Tab */}
                <TabPanel value={tabValue} index={1}>
                  {intakes.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Intake ID</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Service Type</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Selected Services</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Selected Items</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Created Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {intakes.map((intake, index) => (
                            <TableRow key={index} sx={{ "&:hover": { bgcolor: "#f8f8f8" } }}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                                  #{intake.id}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={intake.service_type_id.replace("-", " ").toUpperCase()}
                                  variant="outlined"
                                  sx={{ borderColor: "#000", color: "#000", fontWeight: 600 }}
                                  icon={<PeopleIcon />}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {intake.selected_services.map((service, serviceIndex) => (
                                    <Chip
                                      key={serviceIndex}
                                      label={service.replace("-", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: "0.7rem" }}
                                    />
                                  ))}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box>
                                  {Object.entries(intake.selected_items).map(([serviceId, items]) => (
                                    <Box key={serviceId} sx={{ mb: 1 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
                                        {serviceId.replace("-", " ").replace(/\b\w/g, (l) => l.toUpperCase())}:
                                      </Typography>
                                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.25 }}>
                                        {items.slice(0, 2).map((item, itemIndex) => (
                                          <Chip
                                            key={itemIndex}
                                            label={item}
                                            size="small"
                                            variant="outlined"
                                            sx={{ fontSize: "0.6rem", height: "18px" }}
                                          />
                                        ))}
                                        {items.length > 2 && (
                                          <Chip
                                            label={`+${items.length - 2}`}
                                            size="small"
                                            variant="outlined"
                                            sx={{ fontSize: "0.6rem", height: "18px" }}
                                          />
                                        )}
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={intake.status.replace("-", " ").toUpperCase()}
                                  color={getStatusColor(intake.status) as any}
                                  variant="filled"
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {new Date(intake.created_at).toLocaleDateString()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 8 }}>
                      <AssignmentIcon sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No HR service intakes yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Request HR services to get started
                      </Typography>
                    </Box>
                  )}
                </TabPanel>
              </Paper>
            </Box>
          )}

          {/* Empty State */}
          {projects.length === 0 && intakes.length === 0 && !loading && (
            <Box sx={{ textAlign: "center", py: 10 }}>
              <Avatar sx={{ bgcolor: "#f5f5f5", width: 100, height: 100, mx: "auto", mb: 4 }}>
                <DescriptionIcon sx={{ fontSize: 50, color: "#666" }} />
              </Avatar>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: "#000" }}>
                No requests yet
              </Typography>
              <Typography variant="body1" sx={{ mb: 6, color: "#666", maxWidth: "500px", mx: "auto", lineHeight: 1.6 }}>
                Start transforming your business operations by making your first service request today. Choose from our
                comprehensive range of HR and recruitment solutions.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsModalOpen(true)}
                sx={{
                  bgcolor: "#000",
                  color: "white",
                  px: 4,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#333" },
                }}
              >
                Get Started
              </Button>
            </Box>
          )}

          {/* Floating Action Button */}
          <Fab
            color="primary"
            aria-label="add"
            onClick={() => setIsModalOpen(true)}
            disabled={loading}
            sx={{
              position: "fixed",
              bottom: 32,
              right: 32,
              bgcolor: "#000",
              "&:hover": { bgcolor: "#333" },
              "&:disabled": { bgcolor: "#ccc" },
              width: 72,
              height: 72,
            }}
          >
            <AddIcon sx={{ fontSize: 32 }} />
          </Fab>

          {/* Service Request Modal */}
          <ServiceRequestModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSubmit={handleRequestSubmit}
          />
        </Container>
      </IonContent>
    </IonPage>
  )
}
